import React, { useMemo, useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import { PATHS } from 'Config/Routing/paths'

import { getEnabledCountryChannels } from './gql'
import { fetchClientCampaignsList } from '../../Containers/Campaigns/gql'
import { createSSCampaign } from '../ClientDashboard/CampaignView/gql'
import {
  addAuthParamsToCampaignBuilderUrl,
  getCampaignBuilderPageUrl,
} from '../../Containers/Country/Page/Pipeline/PipelineView/utils'
import { openLinkInSameTab } from '../../Containers/Campaigns/Page/InternalFiles/Container/utils'
import ButtonDropdown from '../ButtonDropdown'

import {
  CAMPAIGN_ENUM_TYPES_TO_TITLE,
  CAMPAIGN_ENUM_TYPES_TO_CAMPAIGN_NAME,
  CAMPAIGN_ENUM_TYPES,
  DocumentFormats,
  EDDM_DOCUMENT_FORMATS,
} from '../../shared/constants'

const NewCampaignDropdown = ({
  disabled = false,
  cypress_id,
  countryId,
  clientId,
  selfServeEnabled,
}) => {
  const history = useHistory()
  const isEddmEnabled = countryId === 14

  const [newCampaignCTAOptions, setNewCampaignCTAOptions] = useState([])
  const [countryCode, setCountryCode] = useState('US')
  const [countrySettings, setCountrySettings] = useState({})

  const getData = async countryId => {
    const req = await getEnabledCountryChannels({ countryId })
    const {
      data: {
        country: {
          countryDefaultSettings: {
            enabledChannelLetterbox,
            enabledChannelPreciseLocations,
            ...restCountrySettings
          },
          code,
        },
      },
    } = await req.json()

    setCountryCode(code)
    setCountrySettings(restCountrySettings)

    if (!selfServeEnabled) {
      return
    }

    const newCampaignCTAOptions = []

    if (isEddmEnabled) {
      // show EDDM only for US clients
      newCampaignCTAOptions.push(CAMPAIGN_ENUM_TYPES.DIRECTMAIL)
    }
    if (enabledChannelLetterbox) {
      newCampaignCTAOptions.push(CAMPAIGN_ENUM_TYPES.LETTERBOX)
    }
    if (enabledChannelPreciseLocations) {
      newCampaignCTAOptions.push(CAMPAIGN_ENUM_TYPES.HANDTOHAND)
    }

    setNewCampaignCTAOptions(newCampaignCTAOptions)
  }

  const handleCreateCampaign = async type => {
    try {
      const clientCampaignsListResponse = await fetchClientCampaignsList({
        clientIds: [clientId],
      })
      const flyerType =
        (countryCode === 'US' || countryCode === 'CA') &&
        type === CAMPAIGN_ENUM_TYPES.LETTERBOX
          ? DocumentFormats.DOOR_HANGER
          : type === CAMPAIGN_ENUM_TYPES.DIRECTMAIL
          ? EDDM_DOCUMENT_FORMATS.BEST_SELLER
          : DocumentFormats.A6

      const flyerWeight = type === CAMPAIGN_ENUM_TYPES.DIRECTMAIL ? null : '250'

      const baPerLocation =
        type === CAMPAIGN_ENUM_TYPES.HANDTOHAND
          ? countrySettings.baPerLocation
          : null

      let campaignCount = clientCampaignsListResponse
        ? clientCampaignsListResponse.length + 1
        : 1

      const campaignNumber =
        campaignCount < 10
          ? `00${campaignCount}`
          : campaignCount >= 10 && campaignCount < 100
          ? `0${campaignCount}`
          : `${campaignCount}`

      const name = `${
        CAMPAIGN_ENUM_TYPES_TO_CAMPAIGN_NAME[type]
      } Campaign - ${campaignNumber}`

      const response = await createSSCampaign({
        clientId,
        name,
        type,
        flyerType,
        flyerWeight,
        baPerLocation,
        lastActiveStep: 'home',
      })
      const { data } = await response.json()
      const { campaignId } = data.createSSCampaign
      const url = getCampaignBuilderPageUrl({
        campaignId,
        clientId,
        lastActiveStep: 'home',
        channel: type,
      })
      const linkWithAuth = addAuthParamsToCampaignBuilderUrl(url, clientId)
      openLinkInSameTab(linkWithAuth)
    } catch (err) {
      throw new Error(err)
    }
  }

  useEffect(() => {
    if (!disabled) {
      getData(countryId)
    }
  }, [disabled])

  const options = useMemo(
    () => [
      {
        label: 'Core campaign',
        onClick: () =>
          history.push(
            `${PATHS.main.dashboard}${PATHS.dashboard.campaigns}${
              PATHS.campaigns.create
            }`,
          ),
      },
      ...newCampaignCTAOptions.map(option => ({
        label: CAMPAIGN_ENUM_TYPES_TO_TITLE[option],
        onClick: () => handleCreateCampaign(option),
        description: 'Promofy Ads',
      })),
    ],
    [newCampaignCTAOptions.length],
  )

  return (
    <ButtonDropdown
      label="New campaign"
      disabled={disabled}
      cypressId={cypress_id}
      options={options}
    />
  )
}

export default NewCampaignDropdown
